import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { presupuestoProformaService } from '../services/presupuestoProformaService';
import { PRESUPUESTOS_PROFORMA_QUERY_KEY } from './usePresupuestosProforma';
import { useAutoguardadoItem } from './useAutoguardadoItem';
import type {
  AccionEstadoPresupuesto,
  ModoImportacionPlantilla,
  ParteItem,
  PresupuestoProforma,
  PresupuestoProformaDatosFormData,
  PresupuestoProformaItem,
  PresupuestoProformaItemFormData,
  TotalesPorParte,
} from '../types';
import type { TipoMoneda } from '@/types/costo';
import { calcularBonificacion } from '@/components/Costos/calculo';
import { aplicarOrden } from '../utils/orden';

/** Recalcula los subtotales por parte y moneda, igual que `subtotalesPorParte` en el backend. */
const calcularTotales = (items: Record<ParteItem, PresupuestoProformaItem[]>): TotalesPorParte => {
  const partes = Object.keys(items) as ParteItem[];
  const monedas: TipoMoneda[] = ['ARS', 'USD'];

  return partes.reduce((totales, parte) => {
    const deLaParte = items[parte] ?? [];

    totales[parte] = monedas.reduce(
      (porMoneda, moneda) => {
        porMoneda[moneda] = deLaParte
          .filter((item) => item.moneda === moneda)
          .reduce((suma, item) => suma + item.monto, 0);
        return porMoneda;
      },
      {} as Record<TipoMoneda, number>
    );

    return totales;
  }, {} as TotalesPorParte);
};

export const detalleQueryKey = (id: string) =>
  [...PRESUPUESTOS_PROFORMA_QUERY_KEY, 'detalle', id] as const;

/**
 * Detalle de un presupuesto independiente, con autoguardado de la edición inline.
 */
export const usePresupuestoProforma = (id: string) => {
  const queryClient = useQueryClient();
  const queryKey = detalleQueryKey(id);

  const { data: presupuesto, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => presupuestoProformaService.obtener(id),
    enabled: !!id,
    staleTime: 0,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: [...PRESUPUESTOS_PROFORMA_QUERY_KEY, 'list'] });
  };

  const buscarItem = useCallback(
    (itemId: string): PresupuestoProformaItem | undefined => {
      const actual = queryClient.getQueryData<PresupuestoProforma>(queryKey);

      return actual
        ? Object.values(actual.items).flat().find((item) => item.id === itemId)
        : undefined;
    },
    [queryClient, queryKey]
  );

  /**
   * Aplica el cambio en la caché antes de que responda el servidor, para que los
   * subtotales se actualicen apenas se edita.
   */
  const aplicarEnCache = useCallback(
    (itemId: string, data: Partial<PresupuestoProformaItem>) => {
      queryClient.setQueryData<PresupuestoProforma | undefined>(queryKey, (actual) => {
        if (!actual) return actual;

        const items = Object.fromEntries(
          Object.entries(actual.items).map(([parte, lista]) => [
            parte,
            (lista as PresupuestoProformaItem[]).map((item) =>
              item.id === itemId ? { ...item, ...data } : item
            ),
          ])
        ) as PresupuestoProforma['items'];

        return { ...actual, items, totales: calcularTotales(items) };
      });
    },
    [queryClient, queryKey]
  );

  const autoguardado = useAutoguardadoItem<Partial<PresupuestoProformaItemFormData>>({
    guardar: async (itemId, data) => {
      const actualizado = await presupuestoProformaService.actualizarItem(id, itemId, data);
      aplicarEnCache(itemId, actualizado);
      return actualizado;
    },
  });

  /** Handler para la edición inline: pinta el cambio y lo agenda con debounce. */
  const actualizarItemConAutoguardado = useCallback(
    async (itemId: string, data: Partial<PresupuestoProformaItemFormData>) => {
      const {
        modo_calculo: _modo,
        porcentaje: _porcentaje,
        porcentaje_adicional: _porcentajeAdicional,
        suma_fija: _sumaFija,
        suma_fija_moneda: _sumaFijaMoneda,
        importe_real: importeReal,
        bonificacion_tipo: bonificacionTipo,
        bonificacion_valor: bonificacionValor,
        destino_pago_id: destinoPagoId,
        moneda_impresion: monedaImpresion,
        monto,
        ...visibles
      } = data;
      const cambios: Partial<PresupuestoProformaItem> = { ...visibles };

      if (destinoPagoId !== undefined) cambios.destinoPagoId = destinoPagoId;
      if (monedaImpresion !== undefined) cambios.monedaImpresion = monedaImpresion;

      // Editar el monto a mano convierte un ítem porcentual en fijo (igual que el
      // backend).
      if (monto !== undefined) {
        Object.assign(cambios, {
          modoCalculo: 'fijo',
          porcentaje: null,
          porcentajeAdicional: null,
          sumaFija: null,
          sumaFijaMoneda: null,
        });
      }

      // El monto editado es el bruto: el neto le descuenta la bonificación, la
      // nueva si cambió en la misma edición.
      if (monto !== undefined || 'bonificacion_tipo' in data) {
        const item = buscarItem(itemId);
        const bruto = monto ?? item?.montoBruto ?? item?.monto ?? 0;
        const tipo = 'bonificacion_tipo' in data ? (bonificacionTipo ?? null) : (item?.bonificacionTipo ?? null);
        const valor = 'bonificacion_tipo' in data ? (bonificacionValor ?? null) : (item?.bonificacionValor ?? null);
        const bonificacion = calcularBonificacion(bruto, tipo, valor);

        Object.assign(cambios, {
          montoBruto: bruto,
          monto: Math.round((bruto - bonificacion) * 100) / 100,
          bonificacionTipo: tipo,
          bonificacionValor: tipo ? valor : null,
          bonificacionImporte: bonificacion,
        });
      }

      // Un ingreso no tiene importe real: el backend lo descarta al cambiar el tipo.
      if (data.tipo === 'ingreso') {
        cambios.importeReal = null;
      } else if ('importe_real' in data) {
        cambios.importeReal = importeReal ?? null;
      }

      aplicarEnCache(itemId, cambios);
      autoguardado.programarGuardado(itemId, data);
    },
    [aplicarEnCache, autoguardado, buscarItem]
  );

  const actualizarMutation = useMutation({
    mutationFn: (data: PresupuestoProformaDatosFormData) =>
      presupuestoProformaService.actualizar(id, data),
    onSuccess: invalidar,
  });

  const crearItemMutation = useMutation({
    mutationFn: (data: PresupuestoProformaItemFormData) =>
      presupuestoProformaService.crearItem(id, data),
    onSuccess: invalidar,
  });

  /** Cambia el cálculo de un ítem sin debounce: el monto resultante lo devuelve la API. */
  const actualizarCalculoItemMutation = useMutation({
    mutationFn: ({
      itemId,
      data,
    }: {
      itemId: string;
      data: Pick<
        PresupuestoProformaItemFormData,
        'modo_calculo' | 'porcentaje' | 'porcentaje_adicional' | 'suma_fija' | 'suma_fija_moneda'
      >;
    }) => presupuestoProformaService.actualizarItem(id, itemId, data),
    onSuccess: invalidar,
  });

  const importarPlantillaMutation = useMutation({
    mutationFn: async ({
      plantillaId,
      modo,
    }: {
      plantillaId: string;
      modo?: ModoImportacionPlantilla;
    }) => {
      await autoguardado.guardarAhora();
      return presupuestoProformaService.importarPlantilla(id, plantillaId, modo);
    },
    onSuccess: invalidar,
  });

  const eliminarItemMutation = useMutation({
    mutationFn: (itemId: string) => presupuestoProformaService.eliminarItem(id, itemId),
    onSuccess: invalidar,
  });

  const reordenarItemsMutation = useMutation({
    mutationFn: ({ ids }: { parte: ParteItem; ids: string[] }) =>
      presupuestoProformaService.reordenarItems(id, ids),
    // Si no se guardó, se vuelve a cargar el orden del servidor. En el éxito no se
    // invalida: la caché ya tiene el orden nuevo y no se pisa un autoguardado pendiente.
    onError: invalidar,
  });

  /**
   * Guarda el orden de impresión de una caja. Se aplica en la caché antes de la
   * request para que la lista no vuelva un instante al orden anterior.
   */
  const reordenarItems = (parte: ParteItem, ids: string[]) => {
    queryClient.setQueryData<PresupuestoProforma | undefined>(queryKey, (actual) =>
      actual
        ? { ...actual, items: { ...actual.items, [parte]: aplicarOrden(actual.items[parte] ?? [], ids) } }
        : actual
    );

    return reordenarItemsMutation.mutateAsync({ parte, ids });
  };

  const cambiarEstadoMutation = useMutation({
    mutationFn: async (accion: AccionEstadoPresupuesto) => {
      // Nada queda sin guardar antes de un cambio de estado.
      await autoguardado.guardarAhora();

      if (accion === 'confirmar') return presupuestoProformaService.confirmar(id);
      if (accion === 'borrador') return presupuestoProformaService.volverABorrador(id);
      if (accion === 'desarchivar') return presupuestoProformaService.desarchivar(id);
      return presupuestoProformaService.archivar(id);
    },
    onSuccess: invalidar,
  });

  return {
    presupuesto,
    isLoading,
    error,

    actualizarPresupuesto: actualizarMutation.mutateAsync,
    crearItem: crearItemMutation.mutateAsync,
    eliminarItem: eliminarItemMutation.mutateAsync,
    actualizarItem: actualizarItemConAutoguardado,
    actualizarCalculoItem: actualizarCalculoItemMutation.mutateAsync,
    importarPlantilla: importarPlantillaMutation.mutateAsync,
    reordenarItems,
    cambiarEstado: cambiarEstadoMutation.mutateAsync,

    isActualizando: actualizarMutation.isPending,
    isCreandoItem: crearItemMutation.isPending,
    isEliminandoItem: eliminarItemMutation.isPending,
    isActualizandoCalculo: actualizarCalculoItemMutation.isPending,
    isImportandoPlantilla: importarPlantillaMutation.isPending,
    isCambiandoEstado: cambiarEstadoMutation.isPending,

    autoguardado,
  };
};
